import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { messageApi } from '@/api';
import { resolveFileUrl } from '@/api/client';
import { Card, CardHeader, Icon, LoadingState, useToast } from '@/components/ui';
import { Composer } from '@/features/messages/Composer';
import { Thread } from '@/features/messages/Thread';
import { useIsTyping } from '@/features/realtime/useRealtime';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';

/**
 * The conversation about one application, on the application's own screen.
 *
 * Works from either side. The company screen already knows the thread's id
 * and passes `conversationId`; the student screen does not, so when the prop
 * is left out the thread is looked up by application. Either way, sending
 * falls back to sending *by application* while there is no thread — which is
 * how a conversation is created.
 *
 * The lookup's key sits under `['conversations']`, so a realtime frame (which
 * invalidates that prefix) is what makes a thread the other side just started
 * appear here.
 */
export function ApplicationThread({
  applicationId,
  conversationId: knownId,
  candidateName,
  title = 'Messages',
  description,
  messagesPath = paths.company.messages,
  // Set when the candidate cannot be written to (their account is
  // suspended): the thread stays readable and the composer is replaced.
  disabledReason = null,
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [downloadingId, setDownloadingId] = useState(null);
  const lookupKey = ['conversations', 'application', applicationId];

  const lookup = useQuery({
    queryKey: lookupKey,
    queryFn: () => messageApi.getApplicationConversation(applicationId, { page: 1, page_size: 50 }),
    enabled: knownId === undefined && Boolean(applicationId),
  });
  const conversationId = knownId ?? lookup.data?.id ?? null;
  const isTyping = useIsTyping(conversationId);

  const thread = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => messageApi.getConversation(conversationId, { page: 1, page_size: 50 }),
    enabled: Boolean(conversationId),
  });

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
    queryClient.invalidateQueries({ queryKey: ['messages-unread'] });
    // The company screen owns `conversation_id`, and the first message is
    // what gives it one — so this refetch is how the thread appears there.
    queryClient.invalidateQueries({ queryKey: ['company-application', applicationId] });
    if (conversationId) {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
    }
  }

  const send = useMutation({
    mutationFn: (body) =>
      conversationId
        ? messageApi.sendMessage(conversationId, body)
        : messageApi.sendAboutApplication(applicationId, body),
    onSuccess: refresh,
    onError: (error) => toast.error(errorMessage(error)),
  });

  const attach = useMutation({
    mutationFn: ({ file, caption }) => messageApi.sendAttachment(conversationId, file, caption),
    onSuccess: refresh,
    onError: (error) => toast.error(errorMessage(error)),
  });

  async function download(attachment) {
    setDownloadingId(attachment.id);
    try {
      const link = await messageApi.getAttachmentLink(conversationId, attachment.id);
      window.open(resolveFileUrl(link.url), '_blank', 'noopener');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setDownloadingId(null);
    }
  }

  const isLoading = (knownId === undefined && lookup.isPending) || thread.isLoading;

  return (
    <Card>
      <CardHeader
        title={title}
        description={description ?? `Talk to ${candidateName} about this application.`}
        action={
          conversationId ? (
            <Link to={`${messagesPath}?c=${conversationId}`} className="text-brand-600 text-sm">
              Open in Messages
            </Link>
          ) : null
        }
      />

      {isLoading ? (
        <LoadingState label="Loading conversation…" />
      ) : conversationId ? (
        <div className="max-h-80 overflow-y-auto pr-1">
          <Thread
            messages={thread.data?.messages ?? []}
            isTyping={isTyping}
            onDownload={download}
            downloadingId={downloadingId}
          />
        </div>
      ) : (
        <p className="text-ink-muted text-sm">
          No messages yet. The first one you send starts the conversation.
        </p>
      )}

      {disabledReason ? (
        <p className="border-line text-ink-muted mt-4 flex gap-2 border-t pt-3 text-sm">
          <Icon name="suspended" size={16} className="text-danger-600 mt-0.5 shrink-0" />
          <span>{disabledReason}</span>
        </p>
      ) : (
        <Composer
          conversationId={conversationId}
          isSending={send.isPending || attach.isPending}
          onSend={(body) => send.mutateAsync(body)}
          onAttach={
            conversationId
              ? (file, caption) => attach.mutateAsync({ file, caption })
              : // Attaching needs a thread to attach to, and a thread is created
                // by its first message. Sending one text message is the way in.
                async () => {
                  toast.error('Send a message first, then you can attach a file.');
                }
          }
        />
      )}
    </Card>
  );
}
